import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../services/api";
import { toast } from "sonner";
import LoadingSpinner from "../components/LoadingSpinner";

const LINKS = {
  TERMS_OF_SERVICE: "https://varicash.in/terms",
  PRIVACY_POLICY: "https://varicash.in/privacy",
} as const;

const WelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSendOTP = (e: React.FormEvent) => {
    e.preventDefault();

    if (!phoneNumber || phoneNumber.length !== 10) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }

    setIsLoading(true);

    // Navigate immediately (same as Android: navigate first, API call in background)
    setTimeout(() => {
      navigate("/otp-verification", { state: { phoneNumber } });

      // Fire API call in background after navigation
      apiService
        .post("/auth/send-otp/", {
          phone_number: phoneNumber,
          device_type: "web",
        })
        .then((response) => {
          const data = response.data;
          if (!data?.success) {
            toast.error(data?.message || "Failed to send OTP. Please try again.");
            navigate("/login");
          }
        })
        .catch(() => {
          toast.error("Failed to connect to server. Please check your internet connection.");
          navigate("/login");
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, 500);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col max-w-md mx-auto">
      {/* Main Content */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12">
        <div className="max-w-sm mx-auto w-full space-y-8">
          {/* Logo Section */}
          <div className="text-center">
            {/* Varicash Logo */}
            <div className="flex justify-center mb-6">
              <img
                src="/varicashLogo.png"
                alt="Varicash Logo"
                className="h-24 w-auto"
              />
            </div>

            {/* Welcome Text */}
            <h1 className="text-2xl font-semibold text-gray-900 mb-3">
              Welcome to Varicash
            </h1>
            <p className="text-gray-500 text-sm leading-relaxed">
              Your trusted partner for stress-free borrowing.
            </p>
          </div>

          {/* Phone Number Form */}
          <form onSubmit={handleSendOTP} className="space-y-6">
            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700 mb-3"
              >
                Phone No
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="text-gray-900 text-base font-medium">
                    +91
                  </span>
                </div>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  className="w-full pl-16 pr-4 py-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-400"
                  placeholder="Enter Phone Number"
                  value={phoneNumber}
                  onChange={(e) =>
                    setPhoneNumber(
                      e.target.value.replace(/\D/g, "").slice(0, 10)
                    )
                  }
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Send OTP Button */}
            <button
              type="submit"
              disabled={isLoading || phoneNumber.length !== 10}
              className={`w-full text-white font-medium py-4 px-6 rounded-lg transition-colors flex items-center justify-center text-base disabled:cursor-not-allowed ${
                phoneNumber.length === 10 && !isLoading
                  ? "bg-primary-600 hover:bg-primary-700"
                  : "bg-gray-300"
              }`}
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Sending...
                </>
              ) : (
                "Send OTP"
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 pb-8">
        <div className="max-w-sm mx-auto text-center">
          <p className="text-xs text-gray-500 leading-relaxed">
            By continuing you accept our{" "}
            <a
              href={LINKS.TERMS_OF_SERVICE}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 underline hover:text-primary-700"
            >
              Terms of Service
            </a>
            . Also learn how we process your data in our{" "}
            <a
              href={LINKS.PRIVACY_POLICY}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 underline hover:text-primary-700"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default WelcomePage;
